import * as React from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Rating,
  Stack,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate } from "react-router-dom";
import { Talent } from "../../types/users";
import { formatMoney, nl2br } from "../../utils/helper";
import { useGetTalentByIdQuery } from "../../services/talent.service";
import dayjs from "dayjs";
import { GlassDrawer, Button, OrangeLinearProgress } from "../../components/global";

interface TalentDrawerProps {
  data: Talent | null;
  forceRefresh?: boolean;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TalentDrawer(props: TalentDrawerProps) {
  const { data: dataProp, forceRefresh = false, open, setOpen } = props;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = React.useState(dataProp);

  const {
    data: dataApi,
    isError,
    isLoading,
    isFetching,
  } = useGetTalentByIdQuery(dataProp?.id!, {
    skip: !forceRefresh || !dataProp,
    refetchOnMountOrArgChange: true,
  });

  React.useEffect(() => {
    if (!!dataApi && !isError) setData(dataApi);
    else setData(dataProp);
  }, [dataApi, dataProp, isError]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      contentProps={{
        sx: {
          gap: 0,
          pt: { xs: 8, sm: 10 },
          position: "relative",
        },
      }}
    >
      {isMobile && (
        <Box
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 8 },
            right: 8,
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <ClearIcon />
          </IconButton>
        </Box>
      )}
      {(isLoading || isFetching) && <OrangeLinearProgress sx={{ mb: 2 }} />}
      <Stack direction={"row"} sx={{ p: 2 }} alignItems="center" spacing={2}>
        <Avatar
          alt={`${data?.first_name}`}
          src={
            isLoading || isFetching ? "" : `${data?.photo?.[0]?.original_url}`
          }
          sx={{
            width: 100,
            height: 100,
            bgcolor: "#F4A259",
            fontSize: 40,
          }}
        >
          {data?.first_name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#F4A259",
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {data?.first_name} {data?.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {data?.city || "Unknown"}, {data?.state || "Unknown"} {data?.zip || ""}
          </Typography>
          <Rating
            value={data?.total_rating || 0}
            readOnly
            precision={0.5}
            size="small"
            emptyIcon={
              <StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />
            }
            sx={{ mb: 0.5 }}
          />
          <Typography variant="body2" color="text.secondary">
            Balance:{" "}
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: "#F4A259" }}>
              ${!!data?.balance_decrypt
                ? formatMoney(data?.balance_decrypt || 0)
                : "0"}
            </Typography>
          </Typography>
        </Box>
      </Stack>
      {data?.bio && (
        <>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: 14,
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              {nl2br(data.bio)}
            </Typography>
          </Box>
          <Divider />
        </>
      )}
      <List sx={{ py: 0 }}>
        <ListItem sx={{ flexDirection: "column", alignItems: "flex-start", px: 2, py: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#F4A259",
              mb: 1,
            }}
          >
            Skills
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {!data?.skills || data?.skills?.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                No skills added
              </Typography>
            ) : (
              data?.skills?.map((it) => (
                <Chip
                  label={it.name}
                  key={it.id}
                  size="small"
                  sx={{
                    bgcolor: "rgba(244, 162, 89, 0.1)",
                    color: "#F4A259",
                    fontWeight: 500,
                  }}
                />
              ))
            )}
          </Box>
        </ListItem>
        <Divider />
        <ListItem sx={{ flexDirection: "column", alignItems: "flex-start", px: 2, py: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#F4A259",
              mb: 1,
            }}
          >
            Languages
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {!data?.languages || data?.languages?.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                No languages added
              </Typography>
            ) : (
              data?.languages?.map((it) => (
                <Chip
                  label={it.name}
                  key={it.id}
                  size="small"
                  sx={{
                    bgcolor: "rgba(244, 162, 89, 0.1)",
                    color: "#F4A259",
                    fontWeight: 500,
                  }}
                />
              ))
            )}
          </Box>
        </ListItem>
        <Divider />
        <ListItem sx={{ flexDirection: "column", alignItems: "flex-start", px: 2, py: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#F4A259",
              mb: 1,
            }}
          >
            More Info
          </Typography>
          <Stack spacing={1} sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Joined Since
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                {!!data?.created_at
                  ? dayjs(data?.created_at).format("MMM DD, YYYY")
                  : "N/A"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Rate
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                ${data?.min_pay || 0} - ${data?.max_pay || 0} /hr
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Commute Radius
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                {data?.commute_radius || 0} miles
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Allow Background Check
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                {!!data?.allow_background_check ? "Yes" : "No"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Allow Drug Test
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                {!!data?.allow_drug_test ? "Yes" : "No"}
              </Typography>
            </Box>
          </Stack>
        </ListItem>
      </List>
      <Box display="flex" justifyContent="center" sx={{ px: 2, pb: 3, pt: 2 }}>
        <Button
          variant="contained"
          onClick={() => navigate(`/talents/${data?.id}`)}
          fullWidth={isMobile}
          sx={{
            backgroundColor: "#F4A259",
            "&:hover": {
              backgroundColor: "#E8944A",
            },
            fontWeight: 600,
            color: "#ffffff",
          }}
        >
          More Detail
        </Button>
      </Box>
    </GlassDrawer>
  );
}
